import { useEffect } from "react";

import { captureException } from "@sentry/react";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
} from "react-router";

import { version } from "../../../package.json";
import type { Route } from "./+types/root";
import "./app.css";
import { AnalyticsContextType } from "./common/analytics/context";
import { initAnalytics } from "./common/analytics/initAnalytics";
import { pushAnalyticsContext } from "./common/analytics/pushAnalyticsContext";
import { usePlayerId } from "./common/hooks/usePlayerId";
import { initSentry } from "./sentry";

export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
];

const queryClient = new QueryClient();

initSentry();

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Seneca Games</title>
        <Meta />
        <Links />
      </head>
      <body>
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-PQGZV7B5"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          ></iframe>
        </noscript>
        <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
          {children}
        </Sentry.ErrorBoundary>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  const playerId = usePlayerId();

  useEffect(() => {
    initAnalytics();

    pushAnalyticsContext({
      type: AnalyticsContextType.GLOBAL,
      app_version: version,
      user_pseudo_id: playerId,
    });

    console.log(`Welcome to Seneca Games! (V${version})`);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
    </QueryClientProvider>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;

    if (error.status !== 404) {
      captureException(error, {
        tags: {
          errorType: "route_error",
        },
        contexts: {
          response: {
            status: error.status,
            statusText: error.statusText,
            data: error.data,
          },
        },
      });
    }
  } else if (error && error instanceof Error) {
    details = error.message;
    stack = error.stack;

    captureException(error, {
      tags: {
        errorType: "error_boundary",
      },
      contexts: {
        errorDetails: {
          message: error.message,
          stack: error.stack,
          name: error.name,
        },
      },
    });
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
